import type { FC } from 'react';
import { useEffect } from 'react';
import { Grid, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowBackTwoTone from '@mui/icons-material/ArrowBackTwoTone';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ImageSelectionGrid from './ImageSelectionGrid';
import TemplateSelectionGrid, { ITemplate } from './TemplateSelectionGrid';
import type { IMediaLibraryImageModel } from '@/shared/models/media-library/media-library-image.model';
import PropertyDetailsForm from './PropertyDetailsForm';
import type { PlatformType } from '@/editor/shared/constants/available-platforms';
import { useAppSelector } from '@/store/hooks/redux';
import { useGetCreatomateTemplatesQuery } from '@/editor/services/http/creatomate.api';
import CommonButton from '@/shared/components/CommonButton';

interface IGenerateVideoTabProps {
  isNextTab: number;
  onNextTabChange: (value: number) => void;
  imageData: IMediaLibraryImageModel[];
  imageDataLoading: boolean;
  selected: IMediaLibraryImageModel[];
  isSelected: (image: IMediaLibraryImageModel) => boolean;
  onToggleSelect: (image: IMediaLibraryImageModel) => void;
  platform: PlatformType;
  selectedTemplateId: string | null;
  onTemplateSelect: (templateId: string) => void;
  onGenerateVideo: () => void;
}

const GenerateVideoTab: FC<IGenerateVideoTabProps> = ({
  isNextTab,
  onNextTabChange,
  imageData,
  imageDataLoading,
  selected,
  isSelected,
  onToggleSelect,
  platform,
  selectedTemplateId,
  onTemplateSelect,
  onGenerateVideo,
}) => {
  const { t } = useTranslation();
  const propertyExtId = useAppSelector((state) => state.propertyInfoSliceReducer.propertyExtId);
  const storedCompanyId = useAppSelector((state) => state.propertyInfoSliceReducer.companyId);

  const { data: creatomateTemplates } = useGetCreatomateTemplatesQuery({
    propertyExtId,
    companyId: storedCompanyId,
  });

  const templates = creatomateTemplates?.templateData?.templates || [];

  useEffect(() => {
    if (templates && templates.length === 1 && !selectedTemplateId) {
      onTemplateSelect(templates[0].id);
    }
  }, [templates, selectedTemplateId, onTemplateSelect]);

  return (
    <Grid item xs={12}>
      <Box sx={{ display: 'flex', justifyContent: 'end', p: 1 }}>
        {selected.length === 4 && isNextTab !== 2 && (
          <CommonButton
            size="small"
            variant="contained"
            onClick={() => onNextTabChange(2)}
            endIcon={<ArrowForward fontSize="small" />}>
            {t('next')}
          </CommonButton>
        )}
      </Box>

      {isNextTab === 1 && (
        <ImageSelectionGrid
          imageData={imageData}
          imageDataLoading={imageDataLoading}
          selected={selected}
          isSelected={isSelected}
          onToggleSelect={onToggleSelect}
          platform={platform}
        />
      )}

      {isNextTab === 2 && (
        <Grid>
          <PropertyDetailsForm platform={platform} />
          <TemplateSelectionGrid
            templates={templates}
            selectedTemplateId={selectedTemplateId}
            onTemplateSelect={onTemplateSelect}
          />

          <Box
            sx={{
              backgroundColor: 'background.paper',
              bottom: -1,
              display: 'flex',
              justifyContent: 'space-between',
              position: 'sticky',
              p: 1,
            }}>
            <CommonButton
              size="small"
              onClick={() => onNextTabChange(1)}
              startIcon={<ArrowBackTwoTone fontSize="small" />}>
              {t('back')}
            </CommonButton>
            {selectedTemplateId && (
              <CommonButton
                size="small"
                variant="contained"
                onClick={onGenerateVideo}
                endIcon={<ArrowForward fontSize="small" />}>
                {t('generateVideo')}
              </CommonButton>
            )}
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default GenerateVideoTab;
